/**
 * CampaignHealthPanel — Patch 34.2.1d · 34.2.1e
 *
 * A compact health panel that mounts inside the Campaign Room. Consumes
 * `GET /workspaces/{ws}/campaign-runs/{run_id}/health` and renders each
 * backend check verbatim (label + ok flag + safe detail text).
 *
 * Route-ID semantics
 * ------------------
 * The Campaign Room route is `/campaigns/:blueprintId/room` — the URL
 * parameter is the **blueprint id**, NOT a CampaignRun id, NOT a
 * campaign pack id.
 *
 * Patch 34.2.1e · Resolution is now performed via the targeted
 * ``GET /workspaces/{ws}/campaign-runs/by-blueprint/{blueprint_id}``
 * endpoint. The old list-scan (`useCampaignRuns(..., 200)`) was
 * O(N-in-workspace) and would silently fail once a workspace grew past
 * 200 runs. The by-blueprint endpoint is workspace-scoped, returns
 * exactly one match or 404, and never touches a foreign tenant.
 *
 * This panel never auto-heals anything. If a check fails the founder
 * sees the failure with three explicit actions: Retry / Open Review /
 * View Vault / Report Issue.
 */
import { Link } from "react-router-dom";
import { AlertTriangle, CheckCircle2, Loader2, RefreshCw } from "lucide-react";

import { Card, CardContent } from "@/shared/ui/card";
import { Button } from "@/shared/ui/button";
import { Badge } from "@/shared/ui/badge";
import { useCurrentWorkspace } from "@/entities/workspace";
import {
  useCampaignHealth,
  useCampaignRunByBlueprint,
  type RunHealth,
} from "@/entities/campaign-run";
import { ApiError } from "@/shared/api/http";

const HEALTH_TONE: Record<RunHealth, string> = {
  healthy: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
  generating: "bg-amber-500/10 text-amber-400 border-amber-500/30",
  promotion_incomplete: "bg-amber-500/10 text-amber-400 border-amber-500/30",
  reconciliation_failed: "bg-red-500/10 text-red-400 border-red-500/30",
  partial: "bg-amber-500/10 text-amber-400 border-amber-500/30",
  failed: "bg-red-500/10 text-red-400 border-red-500/30",
  blocked: "bg-red-500/10 text-red-400 border-red-500/30",
};

interface Props {
  blueprintId: string;
}

export function CampaignHealthPanel({ blueprintId }: Props): JSX.Element {
  const ws = useCurrentWorkspace();
  // Patch 34.2.1e · targeted lookup (no full list scan).
  const runQuery = useCampaignRunByBlueprint(ws?.id ?? null, blueprintId);
  const health = useCampaignHealth(
    ws?.id ?? null,
    runQuery.data?.id ?? null,
  );

  // ── 1. resolving the run ──────────────────────────────────────────
  if (runQuery.isLoading) {
    return (
      <Card data-testid="campaign-health-panel-loading" className="p-4">
        <div className="flex items-center gap-2 text-sm text-[var(--md-fog)]">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          Loading campaign health…
        </div>
      </Card>
    );
  }

  // 404 from the lookup → the blueprint genuinely has no run yet.
  const runStatus =
    runQuery.error instanceof ApiError ? runQuery.error.status : null;
  const runNotFound = runStatus === 404;

  if (runNotFound || (!runQuery.data && !runQuery.isError)) {
    return (
      <Card data-testid="campaign-health-panel-unavailable" className="border-[color:var(--md-line)] bg-[var(--md-ink-1)]/40">
        <CardContent className="p-4 text-sm">
          <div className="font-semibold text-[var(--md-ivory-0)]">
            Campaign state unavailable
          </div>
          <p className="mt-1 text-xs text-[var(--md-fog)]">
            No CampaignRun projection is linked to this blueprint yet.
            This is expected while the blueprint is still in draft — the
            projection appears once a Campaign Pack exists.
          </p>
        </CardContent>
      </Card>
    );
  }

  if (runQuery.isError) {
    return (
      <Card data-testid="campaign-health-panel-runs-error" className="border-red-500/40 bg-red-500/10">
        <CardContent className="p-4 text-sm">
          <div className="font-semibold text-red-800">
            Couldn&apos;t load campaign state.
          </div>
          <p className="mt-1 text-xs text-red-900/80">
            The canonical projection lookup failed — try again below.
          </p>
          <div className="mt-3">
            <Button
              size="sm"
              variant="outline"
              onClick={() => void runQuery.refetch()}
              data-testid="campaign-health-panel-runs-retry"
            >
              Retry
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // ── 2. resolving the health report ────────────────────────────────
  if (health.isLoading) {
    return (
      <Card data-testid="campaign-health-panel-health-loading" className="p-4">
        <div className="flex items-center gap-2 text-sm text-[var(--md-fog)]">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          Loading health checks…
        </div>
      </Card>
    );
  }

  if (health.isError || !health.data) {
    return (
      <Card data-testid="campaign-health-panel-health-error" className="border-red-500/40 bg-red-500/10">
        <CardContent className="p-4 text-sm">
          <div className="font-semibold text-red-800">
            We couldn&apos;t load campaign health.
          </div>
          <p className="mt-1 text-xs text-red-900/80">
            The health endpoint is unreachable. Try again — no data is at
            risk.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => void health.refetch()}
              data-testid="campaign-health-panel-health-retry"
            >
              Retry
            </Button>
            <Button
              asChild
              size="sm"
              variant="ghost"
              data-testid="campaign-health-panel-health-report"
            >
              <a href="mailto:support@orkexra.com?subject=Campaign%20health%20unreachable">
                Report Issue
              </a>
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  const report = health.data;
  const badgeTone = HEALTH_TONE[report.health];

  return (
    <Card data-testid="campaign-health-panel" className="border-[color:var(--md-line)] bg-[var(--md-ink-1)]/40">
      <CardContent className="p-4">
        <div className="flex items-center justify-between gap-3 mb-3">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-[var(--md-ivory-0)]">
              Campaign Health
            </span>
            <Badge
              variant="outline"
              className={badgeTone}
              data-testid="campaign-health-panel-badge"
            >
              {report.health.replace("_", " ")}
            </Badge>
          </div>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => void health.refetch()}
            data-testid="campaign-health-panel-refresh"
            aria-label="Refresh health"
          >
            <RefreshCw className="h-3.5 w-3.5" aria-hidden />
          </Button>
        </div>

        {report.blocker && (
          <div
            data-testid="campaign-health-panel-blocker"
            className="mb-3 rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-900/80"
          >
            <strong className="text-red-800">Blocker:</strong> {report.blocker}
          </div>
        )}

        <ul className="space-y-1.5" data-testid="campaign-health-panel-checks">
          {report.checks.map((check, i) => (
            <li
              key={i}
              data-testid={`campaign-health-panel-check-${i}`}
              data-check-ok={check.ok ? "true" : "false"}
              className="flex items-start gap-2 text-sm"
            >
              {check.ok ? (
                <CheckCircle2
                  className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400"
                  aria-hidden
                />
              ) : (
                <AlertTriangle
                  className="mt-0.5 h-4 w-4 shrink-0 text-amber-400"
                  aria-hidden
                />
              )}
              <div className="min-w-0">
                <div className={check.ok ? "text-[var(--md-ivory-0)]" : "text-amber-200"}>
                  {check.label}
                </div>
                {check.detail && (
                  <div className="text-xs text-[var(--md-fog)]">
                    {check.detail}
                  </div>
                )}
              </div>
            </li>
          ))}
        </ul>

        <div className="mt-4 flex flex-wrap gap-2">
          <Button
            asChild
            size="sm"
            className="min-h-10 bg-[var(--md-amber)] px-4 font-semibold text-[var(--md-ink-0)] hover:bg-[var(--md-amber)]/90"
            data-testid="campaign-health-panel-open-review"
          >
            <Link to="/campaigns/review">Open Review Queue →</Link>
          </Button>
          <Button
            asChild
            size="sm"
            variant="outline"
            data-testid="campaign-health-panel-view-vault"
          >
            <Link to="/vault">View Vault</Link>
          </Button>
          <Button
            asChild
            size="sm"
            variant="ghost"
            data-testid="campaign-health-panel-report"
          >
            <a href="mailto:support@orkexra.com?subject=Campaign%20health%20issue">
              Report Issue
            </a>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
